import { useEffect } from 'react';

/**
 * Give the Mirror experience its own home-screen / PWA identity for the
 * lifetime of the mounting component, then cleanly restore Mission
 * Control's identity on unmount.
 *
 * It swaps three head signals that "Add to Home Screen" reads:
 *   • <link rel="apple-touch-icon">  — iOS home-screen icon
 *   • <link rel="manifest">          — Android/Chrome install (name + icons)
 *   • <meta name="apple-mobile-web-app-title"> — iOS home-screen label
 *
 * Both iOS Safari and Android Chrome re-read these from the live DOM at
 * install time, so injecting them on Mirror routes is sufficient — no
 * static change to index.html (which currently ships no manifest at all).
 *
 * Pass `enabled = false` to skip (e.g. from a shared layout on a route
 * that should keep the default identity).
 */
export default function useMirrorAppIcons(enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;

    const head = document.head;
    const created = [];
    const restorers = [];

    // Helper: set (or create) a single-instance head element by selector,
    // recording how to restore it on unmount.
    const setOrCreate = (selector, make, apply) => {
      let el = head.querySelector(selector);
      if (el) {
        const snapshot = apply(el, true);
        restorers.push(() => snapshot());
      } else {
        el = make();
        apply(el, false);
        head.appendChild(el);
        created.push(el);
      }
    };

    // apple-touch-icon → Mirror monogram
    setOrCreate(
      'link[rel="apple-touch-icon"]',
      () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'apple-touch-icon');
        l.setAttribute('sizes', '180x180');
        return l;
      },
      (el, existed) => {
        const prev = el.getAttribute('href');
        el.setAttribute('href', '/mirror-apple-touch-icon.png');
        return () => { if (existed && prev != null) el.setAttribute('href', prev); };
      },
    );

    // manifest → Mirror manifest (none exists by default → created & removed)
    setOrCreate(
      'link[rel="manifest"]',
      () => {
        const l = document.createElement('link');
        l.setAttribute('rel', 'manifest');
        return l;
      },
      (el, existed) => {
        const prev = el.getAttribute('href');
        el.setAttribute('href', '/mirror-manifest.json');
        return () => { if (existed && prev != null) el.setAttribute('href', prev); };
      },
    );

    // iOS home-screen label
    setOrCreate(
      'meta[name="apple-mobile-web-app-title"]',
      () => {
        const m = document.createElement('meta');
        m.setAttribute('name', 'apple-mobile-web-app-title');
        return m;
      },
      (el, existed) => {
        const prev = el.getAttribute('content');
        el.setAttribute('content', 'Mirror');
        return () => { if (existed && prev != null) el.setAttribute('content', prev); };
      },
    );

    return () => {
      restorers.forEach((r) => r());
      created.forEach((el) => el.remove());
    };
  }, [enabled]);
}
